import React from 'react';
import { User, Mail, ShieldCheck, RefreshCw, Trash2, Loader2, Boxes } from 'lucide-react';

/**
 * Phase 1D.8 — Tenant user list presentation.
 * Phase 1F.2 — added invite lifecycle actions (resend / delete) and an
 * 'expired' status.
 *
 * Display of users belonging to one tenant. Status pill is derived
 * client-side from is_active + has_password + invite_expires_at (per the
 * backend endpoint's contract). Four possible states:
 *
 *   • invited  — invite sent, not yet accepted, not yet expired
 *                (is_active=false, has_password=false, expiry in future)
 *   • expired  — invite sent but past its expiry window — needs a resend
 *                (is_active=false, has_password=false, expiry past/absent)
 *   • active   — accepted invite, password set, logging in normally
 *                (is_active=true, has_password=true)
 *   • inactive — was deactivated by an admin (is_active=false, but
 *                has_password=true, meaning they previously accepted)
 *
 * Write actions: for any not-yet-active user (invited/expired) the row
 * exposes Resend (re-issues a fresh invite, which invalidates the old
 * link) and Delete (remove a mis-addressed pending invite). These are
 * wired to the parent via the onResend / onDelete props; the parent owns
 * the API calls, confirmation modal, and refetch. Buttons are hidden
 * entirely when those props are not supplied, preserving the original
 * read-only behavior for any other caller.
 *
 * Props
 *   users       — User[] with fields: user_id, email, name, role,
 *                 is_active, is_email_verified, has_password,
 *                 invite_expires_at?, created_at, last_login_at?
 *   onResend    — (user) => void   optional; enables the Resend action
 *   onDelete    — (user) => void   optional; enables the Delete action
 *   resendingId — string           user_id currently being resent (spinner)
 */
export default function TenantUserList({
  users,
  onResend,
  onDelete,
  onEditAppScope,
  resendingId,
  appLabels,
}) {
  const showActions = Boolean(onResend || onDelete || onEditAppScope);
  // 2I.3b — the Apps column renders only when the caller supplies labels.
  //
  // Absent by default, so the root Tenant Detail Users tab is unchanged:
  // it has no source for app names (sidebar-config returns `apps: []` for
  // root) and a column of raw `app_a984d000d748` ids would be worse than
  // no column. The tenant-facing page passes them.
  const showApps = !!appLabels && Object.keys(appLabels).length > 0;
  return (
    <div className="bg-boss-surface border border-boss-border rounded-xl overflow-hidden">
      {/* Desktop table — md+ */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="border-b border-boss-border">
              {['User', 'Role', ...(showApps ? ['Apps'] : []), 'Status', 'Joined'].map((h) => (
                <th
                  key={h}
                  className="text-left py-3 px-4 text-xs font-medium text-boss-text-muted uppercase tracking-wider"
                >
                  {h}
                </th>
              ))}
              {showActions && (
                <th className="text-right py-3 px-4 text-xs font-medium text-boss-text-muted uppercase tracking-wider">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const status = deriveStatus(u);
              return (
                <tr
                  key={u.user_id}
                  className="border-b border-boss-border last:border-b-0 transition-colors"
                  data-testid={`user-row-${u.user_id}`}
                >
                  <td className="py-3 px-4">
                    <UserCell user={u} />
                  </td>
                  <td className="py-3 px-4">
                    <RolePill role={u.role} />
                  </td>
                  {showApps && (
                    <td className="py-3 px-4">
                      <AppScopeCell user={u} appLabels={appLabels} />
                    </td>
                  )}
                  <td className="py-3 px-4">
                    <StatusPill status={status} />
                  </td>
                  <td className="py-3 px-4 text-boss-text-secondary text-xs">
                    {formatDate(u.created_at)}
                    {u.last_login_at && (
                      <div className="text-[11px] text-boss-text-muted mt-0.5">
                        Last seen {formatDate(u.last_login_at)}
                      </div>
                    )}
                  </td>
                  {showActions && (
                    <td className="py-3 px-4">
                      <RowActions
                        user={u}
                        status={status}
                        onResend={onResend}
                        onDelete={onDelete}
                        onEditAppScope={onEditAppScope}                        resending={resendingId === u.user_id}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards — below md */}
      <div className="md:hidden p-3 space-y-2.5">
        {users.map((u) => {
          const status = deriveStatus(u);
          return (
            <div
              key={u.user_id}
              className="bg-boss-base border border-boss-border rounded-lg p-3.5"
              data-testid={`user-card-${u.user_id}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <UserCell user={u} />
                <StatusPill status={status} className="ml-auto shrink-0" />
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-[10px] text-boss-text-muted uppercase tracking-wider mb-0.5">
                    Role
                  </div>
                  <RolePill role={u.role} />
                </div>
                {showApps && (
                  <div>
                    <div className="text-[10px] text-boss-text-muted uppercase tracking-wider mb-0.5">
                      Apps
                    </div>
                    <AppScopeCell user={u} appLabels={appLabels} />
                  </div>
                )}
                <div>
                  <div className="text-[10px] text-boss-text-muted uppercase tracking-wider mb-0.5">
                    Joined
                  </div>
                  <div className="text-boss-text-secondary">
                    {formatDate(u.created_at)}
                    {u.last_login_at && (
                      <div className="text-[10px] text-boss-text-muted mt-0.5">
                        Last seen {formatDate(u.last_login_at)}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {showActions && (
                <div className="mt-3 pt-3 border-t border-boss-border flex justify-end">
                  <RowActions
                    user={u}
                    status={status}
                    onResend={onResend}
                    onDelete={onDelete}
                    onEditAppScope={onEditAppScope}                    resending={resendingId === u.user_id}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Internal helpers ───────────────────────────────────────────────

/**
 * Which of the tenant's apps this person may see (2I.3b).
 *
 * Reads `app_scope` off the user row, which `list_tenant_users` has
 * projected since 2I.2b. The value semantics are the ones fixed in slice
 * 1 and they are the whole subtlety here:
 *
 *   * absent, null, or `[]` all mean EVERY app, not none. That is how
 *     BOSS behaved before the field existed, and it is what every user
 *     written before 2I still carries. Rendering those as "no apps" would
 *     tell an admin their entire existing roster had been locked out.
 *   * a non-empty list is a narrowing.
 *
 * Ids are resolved through `appLabels` rather than shown raw. An id the
 * map does not know is shown as-is instead of dropped: a scope entry for
 * an app the viewer cannot see is exactly the case worth being visible,
 * since it is the one the viewer cannot explain from their own screen.
 */
function AppScopeCell({ user, appLabels }) {
  const scope = Array.isArray(user?.app_scope) ? user.app_scope.filter(Boolean) : [];

  if (scope.length === 0) {
    return (
      <span
        className="text-xs text-boss-text-muted"
        data-testid={`user-apps-${user.user_id}`}
        data-scope="all"
      >
        All apps
      </span>
    );
  }

  return (
    <div
      className="flex flex-wrap gap-1"
      data-testid={`user-apps-${user.user_id}`}
      data-scope="limited"
    >
      {scope.map((id) => (
        <span
          key={id}
          className="px-1.5 py-0.5 rounded text-[11px] bg-boss-elevated border border-boss-border text-boss-text-secondary"
        >
          {appLabels?.[id] || id}
        </span>
      ))}
    </div>
  );
}

/**
 * Map the boolean fields the backend returns to a single status string
 * the UI renders. Centralised here so the rules are visible in one
 * place rather than scattered in JSX.
 */
function deriveStatus(user) {
  if (user.is_active && user.has_password) return 'active';
  if (!user.is_active && !user.has_password) {
    // Pending invite. Distinguish still-valid from expired using the
    // backend-provided invite_expires_at. Missing/unparseable expiry is
    // treated as expired (safer: prompts a resend rather than implying a
    // working link). Legacy backends that don't send the field will fall
    // here too — but the resend action regenerates everything cleanly.
    const exp = user.invite_expires_at ? new Date(user.invite_expires_at) : null;
    if (!exp || isNaN(exp.getTime()) || exp.getTime() <= Date.now()) {
      return 'expired';
    }
    return 'invited';
  }
  // Was active at some point (has_password true) but is_active false now.
  return 'inactive';
}

/**
 * Per-row invite lifecycle actions. Both Resend and Delete are offered
 * only for a pending user (invited/expired): Resend re-issues a fresh
 * invite (invalidating the old link), Delete removes a mis-addressed
 * pending invite. Neither shows for active/inactive users — full
 * lifecycle management for live accounts lives on the global User
 * Management page. The parent owns confirmation + API calls + refetch.
 */
function RowActions({ user, status, onResend, onDelete, onEditAppScope, resending }) {
  const isPending = status === 'invited' || status === 'expired';
  return (
    <div className="flex items-center justify-end gap-1.5">
      {/*
        2I.3c — offered for EVERY status, unlike Resend and Delete.
        Those two act on an invite, which only a pending row has. App
        access is a property of the person, and the row that most needs
        it is an active one: until this shipped an active tenant user had
        no affordance at all, and the only way to change anything about
        them was the browser console.
      */}
      {onEditAppScope && (
        <button
          type="button"
          onClick={() => onEditAppScope(user)}
          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md text-boss-text-secondary bg-boss-elevated hover:bg-boss-surface border border-boss-border transition-colors"
          data-testid={`tenant-edit-app-scope-${user.user_id}`}
          aria-label={`Change app access for ${user.name || user.email}`}
        >
          <Boxes size={12} />
          Apps
        </button>
      )}
      {onResend && isPending && (
        <button
          type="button"
          onClick={() => onResend(user)}
          disabled={resending}
          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-600/10 hover:bg-violet-100 dark:hover:bg-violet-600/20 disabled:opacity-50 transition-colors"
          data-testid={`tenant-resend-invite-${user.user_id}`}
          aria-label={`Resend invite to ${user.name || user.email}`}
        >
          {resending ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <RefreshCw size={12} />
          )}
          Resend
        </button>
      )}
      {onDelete && isPending && (
        <button
          type="button"
          onClick={() => onDelete(user)}
          className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-600/10 hover:bg-red-100 dark:hover:bg-red-600/20 transition-colors"
          data-testid={`tenant-delete-user-${user.user_id}`}
          aria-label={`Delete pending invite for ${user.name || user.email}`}
        >
          <Trash2 size={12} />
          Delete
        </button>
      )}
    </div>
  );
}

function UserCell({ user }) {
  const initials = (user.name || user.email || '?')
    .split(/\s+/)
    .map((s) => s.charAt(0))
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="flex items-center gap-3 min-w-0">
      <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-600/20 text-violet-700 dark:text-violet-300 flex items-center justify-center shrink-0 text-xs font-semibold">
        {initials || <User size={14} />}
      </div>
      <div className="min-w-0">
        <div className="font-medium text-boss-text truncate">{user.name || '—'}</div>
        <div className="text-[11px] text-boss-text-muted truncate flex items-center gap-1 mt-0.5">
          <Mail size={10} />
          {user.email}
        </div>
      </div>
    </div>
  );
}

function RolePill({ role }) {
  // Friendlier display labels for the roles we expect to see most often.
  const label =
    {
      tenant_admin: 'Tenant Admin',
      tenant_member: 'Tenant Member',
      admin: 'Admin',
      contractor: 'Contractor',
      client: 'Client',
    }[role] || role;

  const isAdminish = role === 'admin' || role === 'tenant_admin';

  return (
    <span
      className={
        'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ' +
        (isAdminish
          ? 'bg-violet-100 dark:bg-violet-600/20 text-violet-700 dark:text-violet-300'
          : 'bg-boss-elevated text-boss-text-secondary')
      }
    >
      {isAdminish && <ShieldCheck size={10} />}
      {label}
    </span>
  );
}

const STATUS_PILL_CLASS = {
  active:
    'bg-emerald-50 dark:bg-emerald-600/10 text-emerald-700 dark:text-emerald-300',
  invited:
    'bg-amber-50 dark:bg-amber-600/10 text-amber-700 dark:text-amber-300',
  expired:
    'bg-rose-50 dark:bg-rose-600/10 text-rose-700 dark:text-rose-300',
  inactive:
    'bg-boss-elevated text-boss-text-muted',
};

const STATUS_LABEL = {
  active: 'Active',
  invited: 'Invited',
  expired: 'Expired',
  inactive: 'Inactive',
};

function StatusPill({ status, className = '' }) {
  return (
    <span
      className={
        'inline-flex text-xs px-2 py-0.5 rounded-full w-fit ' +
        (STATUS_PILL_CLASS[status] || '') +
        (className ? ' ' + className : '')
      }
    >
      {STATUS_LABEL[status] || status}
    </span>
  );
}

const dateFmt = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return dateFmt.format(d);
}
